<script setup>
import { computed } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import { layerBox, renderOrder, zIndexOf } from './sceneGeometry'
import { mediaTypeMeta } from './mediaflowMeta'

/**
 * Static miniature of a flow (or a single media item) with the server's
 * geometry: boxes positioned like the canvas, filled with the source-type
 * tone, images drawn for real. Cheap enough for dozens of tiles.
 */
const props = defineProps({
	/** Flow whose `media` is drawn… */
	flow: { type: Object, default: null },
	/** …or one media item (graphics / inputs tiles). */
	media: { type: Object, default: null },
	canvas: { type: Object, default: () => ({ width: 1920, height: 1080 }) },
	/** Dim the picture (e.g. hidden graphic). */
	dim: { type: Boolean, default: false },
	/** Ignore geometry and fill the box (graphics / inputs tiles, where a 12 % logo would vanish). */
	fit: { type: Boolean, default: false },
})

const items = computed(() => {
	if (props.media) return [props.media]
	return Array.isArray(props.flow?.media) ? props.flow.media.filter((m) => m && m.show !== false) : []
})

const layers = computed(() => {
	const list = items.value
	return renderOrder(list).map((item) => {
		const index = list.indexOf(item)
		const box = props.fit ? { x: 0, y: 0, w: props.canvas.width, h: props.canvas.height } : layerBox(item.transform, props.canvas)
		return { item, meta: mediaTypeMeta(item.type), z: zIndexOf(list, index), style: {
			left: `${(box.x / props.canvas.width) * 100}%`,
			top: `${(box.y / props.canvas.height) * 100}%`,
			width: `${(box.w / props.canvas.width) * 100}%`,
			height: `${(box.h / props.canvas.height) * 100}%`,
			zIndex: zIndexOf(list, index),
		} }
	})
})

const aspect = computed(() => `${props.canvas.width} / ${props.canvas.height}`)
</script>

<template>
	<div class="relative w-full overflow-hidden bg-black" :style="{ aspectRatio: aspect }" :class="dim ? 'opacity-50' : ''" aria-hidden="true">
		<div
			v-for="layer in layers"
			:key="layer.item.id || layer.item.name"
			class="absolute overflow-hidden"
			:style="layer.style"
		>
			<img v-if="layer.item.type === 'image' && layer.item.imageUrl" :src="layer.item.imageUrl" alt="" class="h-full w-full object-contain" :class="fit ? 'p-2' : ''" draggable="false" loading="lazy">
			<div v-else class="flex h-full w-full items-center justify-center" :class="layer.meta.tone">
				<icon-base width="12" height="12" :width-view-box="layer.meta.viewBox" :height-view-box="layer.meta.viewBox" :fill="layer.meta.fill"><component :is="layer.meta.icon" /></icon-base>
			</div>
		</div>
		<div v-if="!layers.length" class="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:20%_33%]" />
	</div>
</template>
